import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/LoadingAnimation.css';

const LoadingAnimation = ({ isLoading }) => {
    const [loadingText, setLoadingText] = useState('Preparing your journey');
    const [dots, setDots] = useState('');
    const [localIsLoading, setLocalIsLoading] = useState(false);
    const overlayRef = useRef(null);

    // Sync local loading state with prop
    useEffect(() => {
        if (isLoading) {
            setLocalIsLoading(true);
        } else {
            // Add a small delay before setting local state to false to allow exit animation
            const timer = setTimeout(() => {
                setLocalIsLoading(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isLoading]);

    // Force hide the overlay if it's still visible after timeout
    useEffect(() => {
        if (!isLoading && overlayRef.current) {
            const forceHideTimer = setTimeout(() => {
                if (overlayRef.current) {
                    overlayRef.current.style.display = 'none';
                    document.body.style.overflow = 'auto';
                    document.documentElement.style.overflow = 'auto';
                    document.body.style.height = 'auto';
                    document.documentElement.style.height = 'auto';
                }
            }, 1000);
            return () => clearTimeout(forceHideTimer);
        }
    }, [isLoading]);

    // Animated dots for loading text
    useEffect(() => {
        if (!localIsLoading) return;

        const interval = setInterval(() => {
            setDots(prev => {
                if (prev.length >= 3) return '';
                return prev + '.';
            });
        }, 300);

        return () => clearInterval(interval);
    }, [localIsLoading]);

    // Cycle through different loading messages
    useEffect(() => {
        if (!localIsLoading) return;

        const messages = [
            'Preparing your journey',
            'Exploring destinations',
            'Packing your bags',
            'Checking the weather',
            'Finding the best deals'
        ];

        let currentIndex = 0;

        const interval = setInterval(() => {
            currentIndex = (currentIndex + 1) % messages.length;
            setLoadingText(messages[currentIndex]);
        }, 2000);

        return () => clearInterval(interval);
    }, [localIsLoading]);

    // Create plane animation path - a full 360-degree orbital path
    const generatePlanePosition = (angle) => {
        const radius = 60;
        // Calculate position on the circle
        const x = -Math.cos(angle * Math.PI / 180) * radius;
        const y = -Math.sin(angle * Math.PI / 180) * radius;
        const rotation = angle - 90;
        return { x, y, rotation };
    };

    // Generate positions for a full circle (clockwise movement)
    const planeKeyframes = Array.from({ length: 60 }, (_, i) => {
        // Map from 0 to 360 degrees (full circle)
        const angle = i * (360 / 59);
        return generatePlanePosition(angle);
    });

    // If not loading, don't render anything
    if (!localIsLoading && !isLoading) return null;

    return (
        <div
            className={`loading-overlay ${!isLoading ? 'loading-fade-out' : ''}`}
            ref={overlayRef}
            style={{
                opacity: isLoading ? 1 : 0,
                transition: 'opacity 0.3s ease-out',
                pointerEvents: isLoading ? 'auto' : 'none'
            }}
        >
            <div className="loading-background">
                <div className="loading-particles">
                    {[...Array(20)].map((_, i) => (
                        <div
                            key={i}
                            className="particle"
                            style={{
                                left: `${Math.random() * 100}%`,
                                top: `${Math.random() * 100}%`,
                                animationDelay: `${Math.random() * 2}s`,
                                animationDuration: `${3 + Math.random() * 5}s`
                            }}
                        ></div>
                    ))}
                </div>
            </div>

            <div className="loading-content">
                <div className="loading-logo">
                    <img src="/Assets/image/excellent-travels-logo.png" alt="Excellent Travels Logo" />
                    <div className="logo-glow"></div>
                </div>

                <div className="loading-globe">
                    <div className="globe-container">
                        <div className="globe">
                            <div className="globe-sphere"></div>
                            <div className="globe-outer"></div>
                        </div>

                        {/* Animated airplane with proper flying motion */}
                        <motion.div
                            className="plane"
                            style={{
                                position: 'absolute',
                                zIndex: 9999,
                                transform: 'translateZ(50px)'
                            }}
                            animate={{
                                x: planeKeyframes.map(kf => kf.x),
                                y: planeKeyframes.map(kf => kf.y),
                                rotate: planeKeyframes.map(kf => kf.rotation)
                            }}
                            transition={{
                                duration: 8,
                                ease: "linear",
                                times: planeKeyframes.map((_, i) => i / (planeKeyframes.length - 1)),
                                repeat: Infinity,
                                repeatType: "loop"
                            }}
                        >
                            <i className="fas fa-plane"></i>

                            {/* Animated trail that follows the plane */}
                            <motion.div
                                className="plane-trail"
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '100%',
                                    transformOrigin: 'left center',
                                    width: '20px'
                                }}
                                animate={{
                                    opacity: [0, 0.8, 0],
                                    width: ['0px', '30px', '10px']
                                }}
                                transition={{
                                    duration: 1,
                                    ease: "easeOut",
                                    repeat: Infinity,
                                    repeatType: "loop"
                                }}
                            />
                        </motion.div>
                    </div>
                </div>

                <div className="loading-progress">
                    <div className="progress-bar">
                        <div className="progress-fill"></div>
                    </div>
                </div>

                <p className="loading-text">
                    {loadingText}<span className="loading-dots">{dots}</span>
                </p>
            </div>
        </div>
    );
};

export default LoadingAnimation;
